<template>
  <aside class="inline-cta" aria-label="Try Workspace369" data-sticky-cta-hide>
    <p class="inline-cta__title">{{ title }}</p>
    <p class="inline-cta__copy">{{ copy }}</p>
    <div class="inline-cta__actions">
      <a
        :href="addReferralToUrl('https://app.workspace369.com/signup')"
        class="inline-cta__primary"
        data-conversion-event="start_free_click"
        :data-conversion-label="`${labelPrefix} ${path} trial`"
        :data-conversion-location="path"
      >Start a 14-day trial</a>
      <NuxtLink
        :to="localePath('/pricing')"
        class="inline-cta__ghost"
        data-conversion-event="pricing_click"
        :data-conversion-label="`${labelPrefix} ${path} pricing`"
        :data-conversion-location="path"
      >Compare plans</NuxtLink>
    </div>
    <TrialDisclosure />
  </aside>
</template>

<script setup>
// In-text CTA for buyer guides (`::inline-cta` in Markdown). Trial wording must stay true:
// the Workspace369 trial needs a card, so never say "no credit card".
defineProps({
  title: { type: String, default: 'Try it with one real client' },
  copy: {
    type: String,
    default: 'Set up one client, one project and one invoice in Workspace369, then check what your client sees in the portal.',
  },
  // Conversion label prefix; the auto-placed mid-article card uses its own.
  labelPrefix: { type: String, default: 'Blog inline CTA' },
});
const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();
const route = useRoute();
const path = computed(() => route.path);
</script>

<style scoped>
/* Rules are scoped under .inline-cta so they beat the article's prose rules
   (`.ed-prose a`, `.ed-prose p`), which otherwise painted the trial button's
   text the same navy as its background. */
.inline-cta { margin: 2rem 0; padding: 1.25rem; border: 1px solid #dbe4ec; border-radius: 0.5rem; background: #f4f8fa; color: #0f172a; }
.inline-cta .inline-cta__title { margin: 0; color: #0f172a; font-size: 1.0625rem; font-weight: 700; line-height: 1.4; }
.inline-cta .inline-cta__copy { margin: 0.4rem 0 0; color: #334155; font-size: 0.9375rem; line-height: 1.55; }
.inline-cta .inline-cta__actions { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1rem; }
.inline-cta .inline-cta__primary, .inline-cta .inline-cta__ghost { display: inline-flex; align-items: center; min-height: 44px; padding: 0 1rem; border-radius: 0.375rem; font-size: 0.9375rem; font-weight: 600; text-decoration: none; transition: background-color 150ms ease, border-color 150ms ease; }
.inline-cta .inline-cta__primary, .inline-cta .inline-cta__primary:hover { background: #0f172a; color: #fff; }
.inline-cta .inline-cta__primary:hover { background: #1e293b; }
.inline-cta .inline-cta__ghost, .inline-cta .inline-cta__ghost:hover { border: 1px solid #94a3b8; color: #0f172a; }
.inline-cta .inline-cta__ghost:hover { border-color: #0f172a; }
/* TrialDisclosure has more than one root, so a scoped class on it never matches: reach it with :deep. Without
   this the article's prose margin (1.05rem above and below) put 36px above "Compare plans" on phones. */
.inline-cta :deep(.trial-disclosure) { margin: 0.55rem 0 0; }
.inline-cta .inline-cta__primary:focus-visible, .inline-cta .inline-cta__ghost:focus-visible { outline: 3px solid #269dbb; outline-offset: 3px; }
.dark .inline-cta { background: #132333; border-color: #364859; color: #edf5fa; }
.dark .inline-cta .inline-cta__title { color: #edf5fa; }
.dark .inline-cta .inline-cta__copy { color: #cbd5e1; }
.dark .inline-cta .inline-cta__primary, .dark .inline-cta .inline-cta__primary:hover { background: #f8fafc; color: #0f172a; }
.dark .inline-cta .inline-cta__primary:hover { background: #e2e8f0; }
.dark .inline-cta .inline-cta__ghost, .dark .inline-cta .inline-cta__ghost:hover { border-color: #64748b; color: #edf5fa; }
.dark .inline-cta .inline-cta__ghost:hover { border-color: #edf5fa; }
/* Phones wrap the two buttons, which put "Compare plans" between the trial button and its terms.
   Unwrap the row so the terms sit directly under the trial button (legal B1.1); wider screens keep the row. */
@media (max-width: 640px) {
  .inline-cta { display: flex; flex-direction: column; align-items: flex-start; }
  .inline-cta .inline-cta__actions { display: contents; }
  .inline-cta .inline-cta__primary { order: 1; margin-top: 1rem; }
  .inline-cta :deep(.trial-disclosure) { order: 2; }
  .inline-cta .inline-cta__ghost { order: 3; margin-top: 0.75rem; }
}
</style>
